<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { Play, Bookmark, Eye, FileText } from 'lucide-vue-next'

export interface ContentItem {
  id: number
  title: string
  slug: string
  description?: string
  thumbnail_url: string | null
  duration: string
  view_count: number
  type: string
  is_free: boolean
  published_at: string
  progress: number
  tutor: {
    id: number
    name: string
    slug: string
    avatar_url: string | null
  }
  subject: {
    id: number
    name: string
    color: string
  } | null
  level: {
    id: number
    name: string
  } | null
  examboard: {
    id: number
    name: string
  } | null
}

defineProps<{
  content: ContentItem
  index?: number
}>()

function getGradientClass(index: number): string {
  const gradients = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-orange-500 to-orange-600',
    'from-teal-500 to-teal-600',
    'from-pink-500 to-pink-600',
  ]
  return gradients[index % gradients.length]
}

function getSubjectBadgeClass(color: string | null): string {
  if (!color) return 'bg-blue-100 text-blue-700'
  // Map common colors to Tailwind classes
  const colorMap: Record<string, string> = {
    '#3B82F6': 'bg-blue-100 text-blue-700',
    '#10B981': 'bg-green-100 text-green-700',
    '#8B5CF6': 'bg-purple-100 text-purple-700',
    '#F59E0B': 'bg-orange-100 text-orange-700',
    '#14B8A6': 'bg-teal-100 text-teal-700',
    '#EC4899': 'bg-pink-100 text-pink-700',
  }
  return colorMap[color] || 'bg-blue-100 text-blue-700'
}

function getButtonClass(index: number): string {
  const colors = [
    'bg-blue-600 hover:bg-blue-700',
    'bg-green-600 hover:bg-green-700',
    'bg-purple-600 hover:bg-purple-700',
    'bg-orange-600 hover:bg-orange-700',
    'bg-teal-600 hover:bg-teal-700',
    'bg-pink-600 hover:bg-pink-700',
  ]
  return colors[index % colors.length]
}

function getProgressBarClass(index: number): string {
  const colors = [
    'bg-blue-600',
    'bg-green-600',
    'bg-purple-600',
    'bg-orange-600',
    'bg-teal-600',
    'bg-pink-600',
  ]
  return colors[index % colors.length]
}
</script>

<template>
  <Link
    :href="`/student/explore/${content.id}`"
    class="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer block"
  >
    <!-- Thumbnail -->
    <div :class="['relative h-44 overflow-hidden bg-gradient-to-br', getGradientClass(index ?? 0)]">
      <img
        v-if="content.thumbnail_url"
        :src="content.thumbnail_url"
        :alt="content.title"
        class="w-full h-full object-cover"
      >
      <div v-else class="absolute inset-0 flex items-center justify-center">
        <Play v-if="content.type === 'video'" class="w-12 h-12 text-white opacity-80" />
        <FileText v-else class="w-12 h-12 text-white opacity-80" />
      </div>

      <!-- New Badge -->
      <div v-if="content.progress === 0 && content.published_at?.includes('day') === false" class="absolute top-3 left-3">
        <span class="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">NEW</span>
      </div>

      <!-- Bookmark Button -->
      <div class="absolute top-3 right-3">
        <button
          class="bg-white/20 text-white p-2 rounded-lg hover:bg-white/30"
          @click.prevent.stop
        >
          <Bookmark class="w-4 h-4" />
        </button>
      </div>

      <!-- Duration / Progress Indicator -->
      <div class="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
        <template v-if="content.progress > 0 && content.progress < 100">
          {{ Math.floor((content.progress / 100) * parseInt(content.duration.split(':')[0])) }}:{{ content.duration.split(':').slice(1).join(':') }} / {{ content.duration }}
        </template>
        <template v-else>
          {{ content.duration }}
        </template>
      </div>
    </div>

    <!-- Content Info -->
    <div class="p-5">
      <!-- Tags -->
      <div class="flex items-center gap-2 mb-3 flex-wrap">
        <span
          v-if="content.subject"
          :class="['text-xs font-semibold px-2 py-1 rounded', getSubjectBadgeClass(content.subject.color)]"
        >
          {{ content.subject.name }}
        </span>
        <span
          v-if="content.level"
          class="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded"
        >
          {{ content.level.name }}
        </span>
        <span
          v-if="content.examboard"
          class="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded"
        >
          {{ content.examboard.name }}
        </span>
        <span class="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded capitalize">
          {{ content.type }}
        </span>
      </div>

      <!-- Title -->
      <h3 class="font-bold text-gray-900 text-base mb-2 line-clamp-2">{{ content.title }}</h3>

      <!-- Tutor & Date -->
      <div class="flex items-center gap-2 mb-3">
        <img
          v-if="content.tutor.avatar_url"
          :src="content.tutor.avatar_url"
          :alt="content.tutor.name"
          class="w-6 h-6 rounded-full border border-gray-200 object-cover"
        >
        <div v-else class="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
          <span class="text-xs text-blue-600 font-semibold">{{ content.tutor.name.charAt(0) }}</span>
        </div>
        <p class="text-sm text-gray-600">{{ content.tutor.name }} &bull; {{ content.published_at }}</p>
      </div>

      <!-- Progress or Status -->
      <template v-if="content.progress > 0 && content.progress < 100">
        <div class="flex items-center justify-between mb-4">
          <div class="flex-1 bg-gray-200 rounded-full h-2 mr-3">
            <div
              :class="['h-2 rounded-full', getProgressBarClass(index ?? 0)]"
              :style="{ width: `${content.progress}%` }"
            ></div>
          </div>
          <span class="text-xs font-semibold text-gray-700">{{ content.progress }}%</span>
        </div>
        <span :class="['w-full inline-block text-center text-white py-2.5 rounded-lg text-sm font-semibold transition-colors', getButtonClass(index ?? 0)]">
          Continue Watching
        </span>
      </template>
      <template v-else-if="content.progress >= 100">
        <div class="flex items-center justify-between mb-4">
          <span class="text-xs text-gray-500 flex items-center gap-1">
            <Eye class="w-3 h-3" /> {{ content.view_count.toLocaleString() }} views
          </span>
          <span class="text-xs font-semibold text-green-600">&#10003; Completed</span>
        </div>
        <span :class="['w-full inline-block text-center text-white py-2.5 rounded-lg text-sm font-semibold transition-colors', getButtonClass(index ?? 0)]">
          Watch Again
        </span>
      </template>
      <template v-else>
        <div class="flex items-center justify-between mb-4">
          <span class="text-xs text-gray-500 flex items-center gap-1">
            <Eye class="w-3 h-3" /> {{ content.view_count.toLocaleString() }} views
          </span>
          <span v-if="content.is_free" class="text-xs font-semibold text-green-600">Free</span>
        </div>
        <span :class="['w-full inline-block text-center text-white py-2.5 rounded-lg text-sm font-semibold transition-colors', getButtonClass(index ?? 0)]">
          Start Watching
        </span>
      </template>
    </div>
  </Link>
</template>
